import { useState, useEffect } from 'react';
import { Calculator, Check, AlertCircle, RotateCw, Network, Zap, Server, Cpu } from 'lucide-react';
import SubnetResultVisualizer from './SubnetCalculatorResults';


const IPv4Calculator = () => {
  // States for IPv4 calculator
  const [ipAddress, setIpAddress] = useState('0.0.0.0');
  const [selectedSubnet, setSelectedSubnet] = useState('24');
  const [networkClass, setNetworkClass] = useState('any');
  const [customSubnetMask, setCustomSubnetMask] = useState('');
  const [customSubnetPrefix, setCustomSubnetPrefix] = useState('');
  const [ipValidation, setIpValidation] = useState({ valid: true, message: 'Enter an IP address' });
  const [calculationState, setCalculationState] = useState('idle'); // idle, calculating, complete, error
  const [showCustomSubnet, setShowCustomSubnet] = useState(false);
  const [detectedClass, setDetectedClass] = useState(null);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  
  // Subnet mask options
  const subnetOptions = [
    { value: "custom", label: "Custom Subnet" },
    { value: "1", label: "/1 (128.0.0.0)" },
    { value: "2", label: "/2 (192.0.0.0)" },
    { value: "3", label: "/3 (224.0.0.0)" },
    { value: "4", label: "/4 (240.0.0.0)" },
    { value: "5", label: "/5 (248.0.0.0)" },
    { value: "6", label: "/6 (252.0.0.0)" },
    { value: "7", label: "/7 (254.0.0.0)" },
    { value: "8", label: "/8 (255.0.0.0)" },
    { value: "9", label: "/9 (255.128.0.0)" },
    { value: "10", label: "/10 (255.192.0.0)" },
    { value: "11", label: "/11 (255.224.0.0)" },
    { value: "12", label: "/12 (255.240.0.0)" },
    { value: "13", label: "/13 (255.248.0.0)" },
    { value: "14", label: "/14 (255.252.0.0)" },
    { value: "15", label: "/15 (255.254.0.0)" },
    { value: "16", label: "/16 (255.255.0.0)" },
    { value: "17", label: "/17 (255.255.128.0)" },
    { value: "18", label: "/18 (255.255.192.0)" },
    { value: "19", label: "/19 (255.255.224.0)" },
    { value: "20", label: "/20 (255.255.240.0)" },
    { value: "21", label: "/21 (255.255.248.0)" },
    { value: "22", label: "/22 (255.255.252.0)" },
    { value: "23", label: "/23 (255.255.254.0)" },
    { value: "24", label: "/24 (255.255.255.0)" },
    { value: "25", label: "/25 (255.255.255.128)" },
    { value: "26", label: "/26 (255.255.255.192)" },
    { value: "27", label: "/27 (255.255.255.224)" },
    { value: "28", label: "/28 (255.255.255.240)" },
    { value: "29", label: "/29 (255.255.255.248)" },
    { value: "30", label: "/30 (255.255.255.252)" },
    { value: "31", label: "/31 (255.255.255.254)" },
    { value: "32", label: "/32 (255.255.255.255)" },
  ];

  // Network class constraints
  const classConstraints = {
    any: [255, 255, 255, 255],
    a: [127, 255, 255, 255],
    b: [191, 255, 255, 255], 
    c: [223, 255, 255, 255],
    d: [239, 255, 255, 255],
    e: [247, 255, 255, 255],
  };

  // Default subnet masks for each class
  const classDefaultSubnets = {
    a: "8",
    b: "16", 
    c: "24",
    d: "8",
    e: "8",
    any: "24"
  };

  // Handle subnet selection change
  useEffect(() => {
    setShowCustomSubnet(selectedSubnet === 'custom');
    if (selectedSubnet !== 'custom') {
      // Clear custom subnet values when standard option is selected
      setCustomSubnetMask('');
      setCustomSubnetPrefix('');
    }
  }, [selectedSubnet]);

   
  // IP address validation function
  const validateIpAddress = (value) => {
    if (!value) {
      setIpAddress('0.0.0.0');
      setIpValidation({ valid: false, message: 'IP address is required' });
      return;
    }

    const segments = value.split('.');
    
    // Basic format validation
    if (segments.length !== 4) {
      setIpAddress(value);
      setIpValidation({ valid: false, message: 'Invalid format. Must be x.x.x.x' });
      return;
    }

    // Validate each octet and apply class constraints
    const validatedSegments = segments.map((segment, index) => {
      const num = parseInt(segment);
      if (isNaN(num)) return '0';
      
      // Apply class constraints
      const maxValue = classConstraints[networkClass][index];
      return Math.min(Math.max(0, num), maxValue).toString();
    });

    const validatedIp = validatedSegments.join('.');
    setIpAddress(validatedIp);
    
    // Additional validation checks
    const allValid = validatedSegments.every(segment => {
      const num = parseInt(segment);
      return !isNaN(num) && num >= 0 && num <= 255;
    });

    // Auto-detect IP class
    const firstOctet = parseInt(validatedSegments[0]);
    let detectedNetworkClass = '';
    
    if (firstOctet >= 1 && firstOctet <= 126) {
      detectedNetworkClass = 'a';
    } else if (firstOctet >= 128 && firstOctet <= 191) {
      detectedNetworkClass = 'b';
    } else if (firstOctet >= 192 && firstOctet <= 223) {
      detectedNetworkClass = 'c';
    } else if (firstOctet >= 224 && firstOctet <= 239) {
      detectedNetworkClass = 'd';
    } else if (firstOctet >= 240 && firstOctet <= 255) {
      detectedNetworkClass = 'e';
    }
    
    setDetectedClass(detectedNetworkClass);
    
    // Auto-set subnet mask based on detected class if network class is 'any'
    if (networkClass === 'any' && detectedNetworkClass && selectedSubnet !== 'custom') {
      setSelectedSubnet(classDefaultSubnets[detectedNetworkClass]);
    }
    
    // Check for private IP ranges
    const secondOctet = parseInt(validatedSegments[1]);
    
    let networkInfo = '';
    if (allValid) {
      if (firstOctet === 10) {
        networkInfo = 'Private IP range (10.0.0.0/8)';
      } else if (firstOctet === 172 && secondOctet >= 16 && secondOctet <= 31) {
        networkInfo = 'Private IP range (172.16.0.0/12)';
      } else if (firstOctet === 192 && secondOctet === 168) {
        networkInfo = 'Private IP range (192.168.0.0/16)';
      } else if (firstOctet === 127) {
        networkInfo = 'Loopback address (127.0.0.0/8)';
      } else if (firstOctet === 169 && secondOctet === 254) {
        networkInfo = 'Link-local address (169.254.0.0/16)';
      } else if (firstOctet >= 224 && firstOctet <= 239) {
        networkInfo = 'Multicast address';
      } else if (firstOctet >= 240) {
        networkInfo = 'Reserved address range';
      } else {
        networkInfo = 'Public IP address';
      }
      
      if (detectedNetworkClass) {
        networkInfo += ` (Class ${detectedNetworkClass.toUpperCase()})`;
      }
    }

    setIpValidation({
      valid: allValid,
      message: allValid ? networkInfo : 'Invalid IP address format'
    });
  };

  // Custom subnet mask validation
  const validateSubnetMask = (value) => {
    const sanitizedValue = value.replace(/[^0-9.]/g, '');
    const segments = sanitizedValue.split('.');
    
    const validatedSegments = segments.map((segment, index) => {
      const num = parseInt(segment);
      return isNaN(num) ? '0' : Math.min(Math.max(0, num), 255).toString();
    }).slice(0, 4);
    
    while (validatedSegments.length < 4) {
      validatedSegments.push('0');
    }
    
    setCustomSubnetMask(validatedSegments.join('.'));
    
    // Try to determine CIDR notation from subnet mask
    if (validatedSegments.every(s => s !== '0')) {
      const prefix = calculatePrefixFromMask(validatedSegments.join('.'));
      if (prefix) setCustomSubnetPrefix(prefix);
    }
  };

  // Calculate CIDR prefix from subnet mask
  const calculatePrefixFromMask = (mask) => {
    const octets = mask.split('.');
    if (octets.length !== 4) return null;
    
    let binary = '';
    octets.forEach(octet => {
      const binaryOctet = parseInt(octet).toString(2).padStart(8, '0');
      binary += binaryOctet;
    });
    
    // Count consecutive 1s
    let count = 0;
    for (let char of binary) {
      if (char === '1') count++;
      else break;
    }
    
    // Verify it's a valid subnet mask (all 1s followed by all 0s)
    const valid = binary.slice(0, count).split('').every(bit => bit === '1') && 
                 binary.slice(count).split('').every(bit => bit === '0');
                 
    return valid ? count.toString() : null;
  };

  // Calculate subnet mask from CIDR prefix
  const calculateMaskFromPrefix = (prefix) => {
    const prefixNum = parseInt(prefix);
    if (isNaN(prefixNum) || prefixNum < 0 || prefixNum > 32) return null;
    
    const fullBits = Math.floor(prefixNum / 8);
    const partialBits = prefixNum % 8;
    
    const octets = [0, 0, 0, 0];
    
    // Fill full octets
    for (let i = 0; i < fullBits; i++) {
      octets[i] = 255;
    }
    
    // Calculate partial octet if any
    if (partialBits > 0 && fullBits < 4) {
      octets[fullBits] = 256 - Math.pow(2, 8 - partialBits);
    }
    
    return octets.join('.');
  };

  // Custom subnet prefix validation
  const validateSubnetPrefix = (value) => {
    const sanitizedValue = value.replace(/[^0-9]/g, '');
    const prefixValue = parseInt(sanitizedValue);
    
    if (isNaN(prefixValue)) {
      setCustomSubnetPrefix('');
    } else if (prefixValue < 1) {
      setCustomSubnetPrefix('1');
      setCustomSubnetMask(calculateMaskFromPrefix('1'));
    } else if (prefixValue > 32) {
      setCustomSubnetPrefix('32');
      setCustomSubnetMask(calculateMaskFromPrefix('32'));
    } else {
      setCustomSubnetPrefix(prefixValue.toString());
      setCustomSubnetMask(calculateMaskFromPrefix(prefixValue.toString()));
    }
  };

  // Handle form submission with server communication
const handleIpv4Submit = async (e) => {
  e.preventDefault();
  if (!ipValidation.valid) return;
  
  setCalculationState('calculating');
  setError(null);
  
  try {
    // Determine the subnet mask to send
    let subnetMask = '';
    let cidr = '';
    
    if (selectedSubnet === 'custom') {
      subnetMask = customSubnetMask;
      cidr = customSubnetPrefix;
    } else {
      subnetMask = calculateMaskFromPrefix(selectedSubnet);
      cidr = selectedSubnet;
    }
    
    // Prepare the request data
    const requestData = {
      ipAddress,
      subnetMask,
      cidr: `${cidr}`,
      networkClass: detectedClass || networkClass
    };
    
    // Replace 'http://your-flask-server-url/api/submit' with your actual Flask API endpoint
    const response = await fetch('/v1/calculate-subnet', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });

    

    const responseData = await response.json();
    //console.log("responseData", responseData)
    
    if (responseData.success) {
      
      setResults(responseData);
      setCalculationState('complete');
    } else {
      throw new Error(responseData.error || 'Unknown error occurred');
    }
  } catch (err) {
    setCalculationState('error');
    setError(err.message);
    // Auto-hide error after 5 seconds
    setTimeout(() => {
      setError(null);
    }, 5000);
  }
};

 

  // Helper functions for network calculations
  const calculateNetworkAddress = (ip, mask) => {
    const ipParts = ip.split('.').map(Number);
    const maskParts = mask.split('.').map(Number);
    return ipParts.map((part, i) => part & maskParts[i]).join('.');
  };

  const calculateBroadcastAddress = (ip, mask) => {
    const ipParts = ip.split('.').map(Number);
    const maskParts = mask.split('.').map(Number);
    return ipParts.map((part, i) => part | (~maskParts[i] & 0xff)).join('.');
  };



  // Clear form
  const clearIpv4Form = () => {    
    setIpAddress('0.0.0.0');
    setSelectedSubnet('24');
    setNetworkClass('any');
    setCustomSubnetMask('');
    setCustomSubnetPrefix('');
    setIpValidation({ valid: true, message: 'Enter an IP address' });
    setCalculationState('idle');
    setDetectedClass(null);
    setResults(null);
    setError(null);
  };

  // Effect to validate IP address when network class changes
  useEffect(() => {
    if (ipAddress !== '0.0.0.0') {
      validateIpAddress(ipAddress);
    }
  }, [networkClass]);

  return (
    <div className="mx-auto">
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl shadow-2xl p-8 backdrop-blur-sm border border-gray-700/50 overflow-hidden relative">
        {/* Background decoration */}
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>
        
        {/* Error popup */}
        {error && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50">
            <div className="bg-red-600/90 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between mb-8">
          <h1 className="flex-1 text-2xl md:text-3xl font-bold flex items-center space-x-3 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
            <Network className="h-7 w-7 text-blue-400" />
            <span>IPv4 Subnet Calculator</span>
          </h1>

          <button
            className="relative z-10 shrink-0 cursor-pointer flex items-center space-x-2 bg-gray-700/70 hover:bg-gray-600 px-4 py-2 rounded-lg text-sm transition-all duration-200"
            onClick={clearIpv4Form}
          >
            <RotateCw className="h-4 w-4" />
            <span>Reset</span>
          </button>
        </div>

        <form onSubmit={handleIpv4Submit} className="space-y-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center justify-between">
                <span>IP Address</span>
                <span className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${ipValidation.valid ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                  {ipValidation.valid ? <Check className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                  {ipValidation.valid ? 'Valid' : 'Invalid'}
                </span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={ipAddress}
                  onChange={(e) => validateIpAddress(e.target.value)}
                  className={`w-full px-4 py-3 bg-gray-800/70 border ${ipValidation.valid ? 'border-gray-600 focus:border-blue-500' : 'border-red-500 focus:border-red-500'} rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:outline-none transition-all`}
                  placeholder="e.g. 192.168.1.1"
                />
                {ipAddress && detectedClass && (
                  <div className="absolute right-3 top-3 flex items-center gap-1.5 text-xs font-medium text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded">
                    <Cpu className="h-3 w-3" />
                    Class {detectedClass.toUpperCase()}
                  </div>
                )}
              </div>
              {ipAddress && (
                <p className={`text-xs mt-1 ${ipValidation.valid ? 'text-green-400' : 'text-red-400'}`}>
                  {ipValidation.message}
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Subnet Mask</label>
              <select
                value={selectedSubnet}
                onChange={(e) => setSelectedSubnet(e.target.value)}
                className="w-full px-4 py-3 bg-gray-800/70 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 focus:outline-none transition-all"
              >
                {subnetOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {detectedClass && networkClass === 'any' && selectedSubnet !== 'custom' && (
                <p className="text-xs text-blue-400 mt-1 flex items-center gap-1">
                  <Zap className="h-3 w-3" />
                  Auto-selected subnet for Class {detectedClass.toUpperCase()}
                </p>
              )}
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Network Class</label>
              <div className="grid grid-cols-3 sm:flex sm:flex-wrap gap-3">
                {['any', 'a', 'b', 'c', 'd', 'e'].map((cls) => (
                  <label 
                    key={cls}
                    className={`flex items-center justify-center px-4 py-2 rounded-lg cursor-pointer transition-all ${
                      networkClass === cls 
                        ? 'bg-blue-600/40 border border-blue-500/50 shadow-lg' 
                        : 'bg-gray-700/50 border border-gray-700 hover:bg-gray-700'
                    }`}
                  >
                    <input
                      type="radio"
                      name="networkClass"
                      value={cls}
                      checked={networkClass === cls}
                      onChange={() => setNetworkClass(cls)}
                      className="sr-only"
                    />
                    <span className="capitalize">{cls === 'any' ? 'Any' : `Class ${cls.toUpperCase()}`}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {showCustomSubnet && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">Custom Subnet</label>
                <div className="flex space-x-3">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={customSubnetMask}
                      onChange={(e) => validateSubnetMask(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-800/70 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 focus:outline-none transition-all"
                      placeholder="255.255.255.0"
                    />
                    <p className="text-xs text-gray-400 mt-1">Subnet mask (e.g. 255.255.255.0)</p>
                  </div>
                  <div className="w-24">
                    <input
                      type="text"
                      value={customSubnetPrefix}
                      onChange={(e) => validateSubnetPrefix(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-800/70 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 focus:outline-none transition-all"
                      placeholder="/24"
                    />
                    <p className="text-xs text-gray-400 mt-1">CIDR (1-32)</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Network visualization preview */}
          {ipValidation.valid && ipAddress !== '0.0.0.0' && (
            <div className="bg-gray-800/40 border border-gray-700 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                <Server className="h-4 w-4 text-blue-400" />
                Network Preview
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-800/70 border border-gray-700 rounded-lg p-3">
                  <div className="text-xs text-gray-400">IP Address</div>
                  <div className="text-sm font-medium text-white">{ipAddress}</div>
                </div>
                <div className="bg-gray-800/70 border border-gray-700 rounded-lg p-3">
                  <div className="text-xs text-gray-400">Subnet Mask</div>
                  <div className="text-sm font-medium text-white">
                    {selectedSubnet === 'custom' 
                      ? customSubnetMask || '255.255.255.0' 
                      : selectedSubnet === '8' ? '255.0.0.0'
                      : selectedSubnet === '16' ? '255.255.0.0'
                      : selectedSubnet === '24' ? '255.255.255.0'
                      : selectedSubnet === '30' ? '255.255.255.252'
                      : '255.255.255.255'}
                  </div>
                </div>
                <div className="bg-gray-800/70 border border-gray-700 rounded-lg p-3">
                  <div className="text-xs text-gray-400">CIDR</div>
                  <div className="text-sm font-medium text-white">
                    /{selectedSubnet === 'custom' ? (customSubnetPrefix || '24') : selectedSubnet}
                  </div>
                </div>
                <div className="bg-gray-800/70 border border-gray-700 rounded-lg p-3">
                  <div className="text-xs text-gray-400">Network Class</div>
                  <div className="text-sm font-medium text-white capitalize">
                    {detectedClass ? `Class ${detectedClass.toUpperCase()}` : 'Unknown'}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="pt-4">
            <button
              type="submit"
              disabled={calculationState === 'calculating' || (ipAddress && !ipValidation.valid)}
              className={`w-full py-3 px-6 rounded-xl flex items-center justify-center space-x-3 transition-all ${
                calculationState === 'calculating' || (ipAddress && !ipValidation.valid) 
                  ? 'bg-blue-600/50 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 shadow-lg shadow-blue-700/20'
              }`}
            >
              {calculationState === 'calculating' ? (
                <>
                  <div className="animate-spin h-5 w-5">
                    <RotateCw className="h-5 w-5" />
                  </div>
                  <span>Calculating Subnet...</span>
                </>
              ) : calculationState === 'complete' ? (
                <>
                  <Check className="h-5 w-5" />
                  <span>Calculation Complete</span>
                </>
              ) : (
                <>
                  <Calculator className="h-5 w-5" />
                  <span>Calculate Subnet</span>
                </>
              )}
            </button>
          </div>
        </form>

        
        
        
      </div>
     {/* Results section */}
        {calculationState === 'complete' && results && (
          <div className=" mx-auto p-4">
            <SubnetResultVisualizer results={results} />
          </div>
        )}
    </div>
  );
};

export default IPv4Calculator;